// Copyright (c) Ping. All rights reserved.
//
// Persistence for "has this user seen tour X". Two layers:
//
//   1. LOCAL (authoritative): a `settings_kv` row keyed `tour.<id>.seen`.
//      This is read-before-show and written on any dismissal, so a tour
//      is shown at most once per device and survives reloads. It sits in
//      the same table as `recovery.guideSeen` and is preserved across a
//      perfect-resume sign-out (only a full logout / shared-browser wipe
//      clears it, which is the correct moment to re-show onboarding).
//
//   2. CROSS-DEVICE (best-effort): completing a tour also fires a
//      `com.ping.tour_seen` event over the DeviceGroup so the user's
//      OTHER devices record the same flag and never re-show it. This
//      rides the existing E2EE personal-event channel (same as tasks /
//      read pointers), so it adds no backend endpoint and no per-user
//      write load. The local write is authoritative; the mirror is a
//      nicety that never blocks or throws.
//
// Incoming `com.ping.tour_seen` events from a sibling device are applied
// straight back into this same `settings_kv` key by `webTourSeenPersister`
// (see core/persistence/db.ts), so `readTourSeen` transparently reflects a
// completion that happened on another device.

import { useSessionStore } from '@ping/ui';

import { log } from '@core/log/log';
import { db } from '@core/persistence/db';
import { get, set } from '@core/persistence/settings';
import { sendTourSeen } from '@features/tasks/deviceGroupTaskSync';

import type { TourId } from './tourIds';

interface TourSeenValue {
  readonly seenAt: number;
}

const seenKey = (tourId: TourId): string => `tour.${tourId}.seen`;

/** The live SDK client, or null when we are not fully authenticated yet. */
function currentSdkClient(): unknown | null {
  try {
    const status = useSessionStore.getState().status as {
      readonly kind: string;
      readonly client?: unknown;
    };
    return status.kind === 'authenticated' && status.client != null ? status.client : null;
  } catch {
    return null;
  }
}

/** True once the user has seen (or dismissed) this tour on any of their devices. */
export async function readTourSeen(tourId: TourId): Promise<boolean> {
  try {
    await db.open();
    const value = await get<TourSeenValue>(seenKey(tourId));
    return value !== undefined && value !== null;
  } catch (err) {
    // A read failure must never force onboarding on someone repeatedly, so
    // treat an unreadable flag as "already seen" (fail closed).
    log.debug({ event: 'tour.readSeen.failed', tourId }, (err as Error).message);
    return true;
  }
}

/**
 * Mark a tour seen locally (authoritative) and mirror it to the user's
 * other devices. Best-effort throughout: the local write is what matters,
 * and the DeviceGroup send is fire-and-forget.
 */
export async function markTourSeen(tourId: TourId): Promise<void> {
  try {
    await db.open();
    await set<TourSeenValue>(seenKey(tourId), { seenAt: Date.now() });
  } catch (err) {
    log.debug({ event: 'tour.markSeen.failed', tourId }, (err as Error).message);
  }
  // Never awaited: a slow / absent DeviceGroup must not delay closing the tour.
  void sendTourSeen(currentSdkClient(), tourId);
}
